import type { TEventrIcons } from "@eventr/common-storybook";
import { routes, type RouteTab } from "routing";
import { path } from "routes-utils";
import { CHAT_TABBAR_SVG, HOME_TABBAR_SVG, SETTINGS_TABBAR_SVG, USERS_TABBAR_SVG } from "./native-icons";
import { TAB_STACK_IDS } from "./tab-stack-ids";

export type TabId = RouteTab;

export interface TabDefinition {
  id: TabId;
  /** The path the tab roots at. */
  route: string;
  /** Every path the tab owns. */
  patterns: RegExp[];
  /** English label, and the key `navbarTranslation` resolves. */
  label: string;
  /** The glyph the web bar draws. */
  icon: TEventrIcons;
  /** SVG source for the native bar glyph. */
  nativeSvg: string;
  /** Id of the tab's native stack. */
  stackId: string;
  /** Whether the native bar is hidden on the tab's root view. */
  hidesRootBar: boolean;
  /** Flag that must be true for the native bar to present the tab. */
  requires?: keyof TabAvailability;
}

export interface TabAvailability {
  chatEnabled: boolean;
  teamPageEnabled: boolean;
  /** Guests get only the tabs that carry no `requires`. */
  isGuestMode: boolean;
  /** An account with no team gets only the tabs that carry no `requires`. */
  hasTeams: boolean;
}

function isAvailable(tab: TabDefinition, availability: TabAvailability): boolean {
  if (tab.requires === undefined) return true;
  if (availability.isGuestMode || !availability.hasTeams) return false;
  return availability[tab.requires];
}

const anchored = (patterns: string[]) => patterns.map((pattern) => new RegExp(`^${pattern}`, "i"));

/** The tabs, in the order both bars draw them. */
export const TABS: readonly TabDefinition[] = [
  {
    id: "home",
    route: routes.home.path,
    patterns: anchored([`${routes.home.path}$`, `/${path.edit}(/.*)?$`]),
    label: "Home",
    icon: "Home",
    nativeSvg: HOME_TABBAR_SVG,
    stackId: TAB_STACK_IDS.home,
    hidesRootBar: true,
  },
  {
    id: "chat",
    route: routes.chat.list.path,
    patterns: anchored([`${routes.chat.list.path}(/.*)?$`]),
    label: "Chat",
    icon: "Chat",
    nativeSvg: CHAT_TABBAR_SVG,
    stackId: TAB_STACK_IDS.chat,
    hidesRootBar: true,
    requires: "chatEnabled",
  },
  {
    id: "team",
    route: routes.team.page.path,
    patterns: anchored([`/${path.setup}/${path.addUsers}`, `/${path.team}(/.*)?$`]),
    label: "Team",
    icon: "Users",
    nativeSvg: USERS_TABBAR_SVG,
    stackId: TAB_STACK_IDS.team,
    hidesRootBar: true,
    requires: "teamPageEnabled",
  },
  {
    id: "account",
    route: routes.account.page.path,
    patterns: anchored([`/${path.account}(/.*)?$`]),
    label: "Account",
    icon: "Settings",
    nativeSvg: SETTINGS_TABBAR_SVG,
    stackId: TAB_STACK_IDS.account,
    hidesRootBar: false,
  },
];

export const TAB_BY_ID = Object.fromEntries(TABS.map((tab) => [tab.id, tab])) as Record<TabId, TabDefinition>;

export const HOME_TAB = TAB_BY_ID.home;

/** The tab owning `pathname`, ignoring any query string or fragment. */
export function resolveTabForPath(pathname: string): TabDefinition | undefined {
  const bare = pathname.split(/[?#]/, 1)[0] ?? pathname;
  return TABS.find((tab) => tab.patterns.some((pattern) => pattern.test(bare)));
}

/** The tabs the native bar presents, in order. A tab whose `requires` flag is off is dropped. */
export function presentedTabs(availability: TabAvailability): readonly TabDefinition[] {
  return TABS.filter((tab) => isAvailable(tab, availability));
}

/** Whether the native bar is presenting `id`. */
export function isTabPresented(id: TabId, availability: TabAvailability): boolean {
  return isAvailable(TAB_BY_ID[id], availability);
}
